import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import dayjs from '~/timeInit';

import { Check as CheckIcon, ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';

import { useGetFxRates } from '~/hooks/apis/fx/useGetFxRates';
import { useGetVendorPromotions } from '~/hooks/apis/vendors/useGetVendorPromotions';
import { useGetVendorSummary } from '~/hooks/apis/vendors/useGetVendorSummary';
import { useVendorSearch } from '~/hooks/apis/vendors/useVendorSearch';
import useAppNavigation from '~/hooks/useAppNavigation';
import useDebounced from '~/hooks/useDebounced';
import useUrlQuery from '~/hooks/useQuery';

import type { Dayjs } from '~/services/TimeService';
import { resolveVendorBySalesforceId } from '~/services/vendorLookup';

import type { RollingDuration, VendorSummaryGranularity } from '~/types/services/vendor';

import { isValidSalesforceId } from '~/utils/validateSalesforceId';

import { buildVendorPerformanceEmail } from './buildEmail';
import {
  MONTH_FORMAT,
  defaultPeriod,
  formatPeriodForUrl,
  granularityForPeriod,
  parsePeriod,
  periodLabel,
  recentQuarterOptions,
} from './periodUtils';

const VENDOR_BY_SFID_QUERY_KEY = 'lpc-vendor-by-sfid';
const DEFAULT_ROLLING_DURATION: RollingDuration = 'half_year';

function vendorOptionLabel(vendor: App.Vendor): string {
  return `${vendor.name} (${vendor.sfid})`;
}

export default function LPCVendorPerformancePage() {
  const { replace } = useAppNavigation();
  const location = useLocation();
  const urlQuery = useUrlQuery();
  const queryClient = useQueryClient();

  const urlVendorSfid = urlQuery.get('vendor') ?? '';

  const urlPeriod = urlQuery.get('period') ?? '';
  const granularity: VendorSummaryGranularity = useMemo(() => granularityForPeriod(urlPeriod), [urlPeriod]);

  const period = useMemo(() => {
    if (urlPeriod && parsePeriod(urlPeriod, granularity)) {
      return urlPeriod;
    }
    return defaultPeriod(granularity, DEFAULT_ROLLING_DURATION);
  }, [urlPeriod, granularity]);

  const parsedPeriod = useMemo(() => parsePeriod(period, granularity), [period, granularity]);

  const vendorByIdQuery = useQuery({
    queryKey: [VENDOR_BY_SFID_QUERY_KEY, urlVendorSfid],
    queryFn: () => resolveVendorBySalesforceId(urlVendorSfid),
    enabled: !!urlVendorSfid && isValidSalesforceId(urlVendorSfid),
    staleTime: 5 * 60 * 1000,
  });
  const vendor: App.Vendor | null = vendorByIdQuery.data ?? null;

  const [typedInput, setTypedInput] = useState<string | null>(null);
  const vendorInput = typedInput ?? (vendor ? vendorOptionLabel(vendor) : '');
  const debouncedVendorInput = useDebounced(vendorInput, 300);

  const [copied, setCopied] = useState(false);
  // Total Sales display currency: AUD (BE native) or the vendor's local currency (FX-converted).
  // Treated as a per-vendor default rather than a sticky global preference — selecting a vendor
  // seeds it to that vendor's natural currency (see the effect below); the MM can flip to AUD.
  const [salesCurrencyMode, setSalesCurrencyMode] = useState<'aud' | 'vendor'>('aud');

  const locationRef = useRef(location);
  locationRef.current = location;

  const updateUrl = useCallback(
    (updates: { vendor?: string | null; period?: string | null }) => {
      const current = locationRef.current;
      const params = new URLSearchParams(current.search);
      if ('vendor' in updates) {
        if (updates.vendor) {
          params.set('vendor', updates.vendor);
        } else {
          params.delete('vendor');
        }
      }
      if ('period' in updates) {
        if (updates.period) {
          params.set('period', updates.period);
        } else {
          params.delete('period');
        }
      }
      const newSearch = params.toString();
      replace({ pathname: current.pathname, search: newSearch ? `?${newSearch}` : '' });
    },
    [replace],
  );

  const handleVendorChange = useCallback(
    (next: App.Vendor | null) => {
      if (next) {
        queryClient.setQueryData([VENDOR_BY_SFID_QUERY_KEY, next.sfid], next);
      }
      setTypedInput(null);
      updateUrl({ vendor: next?.sfid ?? null });
    },
    [queryClient, updateUrl],
  );

  const handleGranularityChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, next: VendorSummaryGranularity | null) => {
      if (!next || next === granularity) {
        return;
      }
      // No granularity URL param — switching mode just writes a period of the new shape.
      updateUrl({ period: defaultPeriod(next, DEFAULT_ROLLING_DURATION) });
    },
    [granularity, updateUrl],
  );

  const handleMonthChange = useCallback(
    (next: Dayjs | null) => {
      if (!next?.isValid()) {
        updateUrl({ period: null });
        return;
      }
      updateUrl({ period: formatPeriodForUrl(next, 'month') });
    },
    [updateUrl],
  );

  const handleQuarterChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      updateUrl({ period: event.target.value || null });
    },
    [updateUrl],
  );

  const vendorSearch = useVendorSearch({
    filter: debouncedVendorInput,
    enabled: debouncedVendorInput.length >= 2 && debouncedVendorInput !== vendor?.name,
  });

  const summaryQuery = useGetVendorSummary({
    vendorId: vendor?.sfid ?? null,
    period,
    type: 'lpc',
  });

  const promotionsWindow = useMemo(() => {
    if (!parsedPeriod) {
      return { from: '', to: '' };
    }
    return {
      from: parsedPeriod.start.format('YYYY-MM-DD'),
      to: parsedPeriod.end.format('YYYY-MM-DD'),
    };
  }, [parsedPeriod]);

  const promotionsQuery = useGetVendorPromotions({
    vendorId: vendor?.sfid ?? null,
    from: promotionsWindow.from,
    to: promotionsWindow.to,
    enabled: !!promotionsWindow.from,
  });

  const fxRatesQuery = useGetFxRates();
  const fxRatesMap = useMemo<Record<string, number>>(() => {
    const map: Record<string, number> = {};
    for (const { currency, rate } of fxRatesQuery.data ?? []) {
      map[currency] = rate;
    }
    map.AUD = 1;
    return map;
  }, [fxRatesQuery.data]);

  const vendorCurrency = vendor?.currencyisocode ?? 'AUD';
  // Only offer the vendor-currency option when the vendor bills in a non-AUD currency
  // we actually have an FX rate for — otherwise the toggle would be a no-op.
  const canShowVendorCurrency = vendorCurrency !== 'AUD' && fxRatesMap[vendorCurrency] != null;
  const displayCurrency = salesCurrencyMode === 'vendor' && canShowVendorCurrency ? vendorCurrency : 'AUD';

  // Seed the currency to the selected vendor's default: their own currency when we can convert
  // to it, else AUD. Re-runs on vendor switch (keyed on sfid) so a new vendor starts at their
  // default instead of inheriting the previous vendor's toggle; a manual flip persists until the
  // next switch. Also fires once rates finish loading (canShowVendorCurrency false → true).
  useEffect(() => {
    setSalesCurrencyMode(canShowVendorCurrency ? 'vendor' : 'aud');
  }, [vendor?.sfid, canShowVendorCurrency]);

  const isLoadingEmail = !!vendor && (summaryQuery.isPending || promotionsQuery.isPending || vendorByIdQuery.isPending);

  const generatedEmail = useMemo(() => {
    if (!summaryQuery.data || !vendor || promotionsQuery.isPending) {
      return '';
    }
    return buildVendorPerformanceEmail({
      summary: summaryQuery.data,
      vendorName: vendor.name,
      vendorCurrency: vendor.currencyisocode,
      promotions: promotionsQuery.data?.rows ?? [],
      displayCurrency,
      fxRates: fxRatesMap,
    });
  }, [summaryQuery.data, vendor, promotionsQuery.data, promotionsQuery.isPending, displayCurrency, fxRatesMap]);

  const handleCopy = async () => {
    if (!generatedEmail) {
      return;
    }
    try {
      await navigator.clipboard.writeText(generatedEmail);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API may not be available in all contexts
    }
  };

  const vendorOptions = vendorSearch.data?.result ?? [];

  const monthValue: Dayjs | null = useMemo(() => {
    if (granularity !== 'month') {
      return null;
    }
    const parsed = dayjs(period, MONTH_FORMAT, true);
    return parsed.isValid() ? parsed : null;
  }, [granularity, period]);

  const quarterOptions = useMemo(() => recentQuarterOptions(12), []);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        LPC Vendor Performance — Email Generator
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Generate a performance email for an LPC Tier 1 partner. Pick a vendor, reporting scope, and period, then copy
        the generated email into your email client.
      </Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} mt={3} alignItems={{ xs: 'stretch', md: 'center' }}>
        <Autocomplete<App.Vendor>
          sx={{ flex: 1, minWidth: 300 }}
          value={vendor}
          onChange={(_, value) => handleVendorChange(value)}
          inputValue={vendorInput}
          onInputChange={(_, value) => setTypedInput(value)}
          options={vendorOptions}
          getOptionLabel={vendorOptionLabel}
          isOptionEqualToValue={(option, val) => option.sfid === val.sfid}
          loading={vendorSearch.isFetching || vendorByIdQuery.isFetching}
          filterOptions={(options) => options}
          noOptionsText={debouncedVendorInput.length < 2 ? 'Type at least 2 characters' : 'No vendors found'}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Vendor"
              placeholder="Search by vendor name, vendor ID, or offer BK"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {vendorSearch.isFetching || vendorByIdQuery.isFetching ? <CircularProgress size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <ToggleButtonGroup
          value={granularity}
          exclusive
          color="primary"
          onChange={handleGranularityChange}
          aria-label="Reporting scope"
        >
          <ToggleButton value="month" aria-label="Monthly">
            Monthly
          </ToggleButton>
          <ToggleButton value="quarter" aria-label="Quarterly">
            Quarterly
          </ToggleButton>
          <ToggleButton value="rolling" aria-label="6 Months">
            6 Months
          </ToggleButton>
        </ToggleButtonGroup>

        {granularity === 'month' && (
          <DatePicker
            label="Reporting month"
            views={['year', 'month']}
            openTo="month"
            format="MMMM YYYY"
            value={monthValue}
            onChange={handleMonthChange}
            maxDate={dayjs().startOf('month')}
            slotProps={{ textField: { sx: { minWidth: 220 } } }}
          />
        )}

        {granularity === 'quarter' && (
          <TextField
            select
            label="Reporting quarter"
            value={period}
            onChange={handleQuarterChange}
            sx={{ minWidth: 220 }}
          >
            {quarterOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {periodLabel(option, 'quarter')}
              </MenuItem>
            ))}
          </TextField>
        )}

        {granularity === 'rolling' && (
          <TextField
            label="Reporting period"
            value={periodLabel(period, 'rolling')}
            InputProps={{ readOnly: true }}
            sx={{ minWidth: 220 }}
          />
        )}

        {canShowVendorCurrency && (
          <ToggleButtonGroup
            value={salesCurrencyMode}
            exclusive
            color="primary"
            onChange={(_, next: 'aud' | 'vendor' | null) => next && setSalesCurrencyMode(next)}
            aria-label="Total Sales currency"
          >
            <ToggleButton value="aud" aria-label="Australian dollars">
              AUD
            </ToggleButton>
            <ToggleButton value="vendor" aria-label={`Vendor currency (${vendorCurrency})`}>
              {vendorCurrency}
            </ToggleButton>
          </ToggleButtonGroup>
        )}
      </Stack>

      {summaryQuery.isError && (
        <Box mt={3}>
          <Alert severity="error">
            Failed to load summary: {summaryQuery.error instanceof Error ? summaryQuery.error.message : 'Unknown error'}
          </Alert>
        </Box>
      )}

      {promotionsQuery.isError && (
        <Box mt={3}>
          <Alert severity="error">
            Failed to load promotions:{' '}
            {promotionsQuery.error instanceof Error ? promotionsQuery.error.message : 'Unknown error'}
          </Alert>
        </Box>
      )}

      {vendorByIdQuery.isError && (
        <Box mt={3}>
          <Alert severity="error">
            Could not load the vendor referenced in the URL ({urlVendorSfid}). Pick a vendor manually.
          </Alert>
        </Box>
      )}

      {isLoadingEmail && (
        <Box mt={3} display="flex" alignItems="center" gap={1}>
          <CircularProgress size={18} />
          <Typography variant="body2">Loading…</Typography>
        </Box>
      )}

      {!vendor && !vendorByIdQuery.isFetching && (
        <Box mt={3}>
          <Alert severity="info">Select a vendor to generate the performance email.</Alert>
        </Box>
      )}

      {generatedEmail && (
        <Box mt={3}>
          <Stack direction="row" spacing={2} mb={2} alignItems="center">
            <Button
              variant="contained"
              color="primary"
              startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
              onClick={handleCopy}
            >
              {copied ? 'Copied!' : 'Copy email'}
            </Button>
          </Stack>

          <TextField
            multiline
            minRows={20}
            maxRows={40}
            value={generatedEmail}
            fullWidth
            InputProps={{ readOnly: true }}
          />

          {displayCurrency !== 'AUD' && (
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              * Currency metrics converted from AUD to {displayCurrency} at today&apos;s exchange rate. Historical
              figures are valued at the current rate, so the converted amounts are indicative only.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
